import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faCheckCircle,
  faSpinner,
  faExclamationCircle,
  faArrowCircleRight,
  faShieldAlt,
  faChartLine,
  faCalendarCheck,
  faHandshake,
  faQuestionCircle,
  faFileContract,
  faClipboardCheck,
  faHistory,
  faBalanceScale,
  faMapMarkerAlt,
  faTruck,
  faFileInvoiceDollar,
} from "@fortawesome/free-solid-svg-icons";

const ShipmentRiskAnalyzer = ({ item }) => {
  const [riskScore, setRiskScore] = useState(0);
  const [riskFactors, setRiskFactors] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [isAnalyzing, setIsAnalyzing] = useState(true);
  const [historicalPerformance, setHistoricalPerformance] = useState(null);
  const [selectedFactor, setSelectedFactor] = useState(null);

  // Analyze the shipment for risks on component mount
  useEffect(() => {
    analyzeShipmentRisk();
  }, [item]);

  const analyzeShipmentRisk = () => {
    setIsAnalyzing(true);

    // Simulate API call delay for risk analysis
    setTimeout(() => {
      const analysisResults = performRiskAnalysis(item);
      setRiskScore(analysisResults.score);
      setRiskFactors(analysisResults.factors);
      setRecommendations(analysisResults.recommendations);
      setHistoricalPerformance(analysisResults.historical);
      setIsAnalyzing(false);
    }, 1200);
  };

  // Perform risk analysis on the shipment
  const performRiskAnalysis = (shipment) => {
    const factors = [];
    const recommendations = [];
    let score = 0;

    // 1. Check processing time factors
    const datesData = shipment.header.detail?.dates_data || {};
    const dateInit = datesData.date_init ? new Date(datesData.date_init) : null;
    const dateCrossing = datesData.date_crossing
      ? new Date(datesData.date_crossing)
      : null;

    if (dateInit && dateCrossing) {
      const processingHours = (dateCrossing - dateInit) / (1000 * 60 * 60);

      // Check if processing time is high
      if (processingHours > 400) {
        factors.push({
          id: "high_processing_time",
          name: "High Processing Time",
          description: `Current processing time (${Math.round(
            processingHours
          )} hours) is higher than normal for this type of shipment.`,
          severity: "high",
          icon: faExclamationTriangle,
          impact: "Potential delays in delivery and increased demurrage fees",
          details: `For ${getOperationTypeText(
            shipment.header.operation_type
          )} shipments via entry point ${
            shipment.header.pedimento.pedimento_entry_point
          }, typical processing time is 240-300 hours.`,
        });
        recommendations.push({
          id: "expedite_processing",
          name: "Expedite Processing",
          description: "Contact customs broker to expedite processing",
          icon: faCalendarCheck,
          action: "Contact customs broker",
        });
        score += 30;
      }
    }

    // 2. Check for missing or incomplete documentation
    const hasPedimento = !!shipment.header.pedimento.pedimento_number;
    if (!hasPedimento) {
      factors.push({
        id: "missing_pedimento",
        name: "Missing Pedimento Number",
        description:
          "Pedimento number is not available, which may delay processing.",
        severity: "high",
        icon: faExclamationTriangle,
        impact: "High risk of crossing delays",
        details: "Pedimento documentation is required for customs clearance.",
      });
      recommendations.push({
        id: "request_pedimento",
        name: "Request Pedimento Documentation",
        description:
          "Follow up with customs broker for pedimento documentation",
        icon: faFileContract,
        action: "Contact customs agent",
      });
      score += 40;
    }

    // 3. Check status and timing factors
    const status = shipment.header.detail?.traffic_data?.status;
    const isOpen = status !== "Pagado";
    if (isOpen && dateInit) {
      const today = new Date();
      const daysSinceInit = Math.round(
        (today - dateInit) / (1000 * 60 * 60 * 24)
      );

      if (daysSinceInit > 15) {
        factors.push({
          id: "extended_open_status",
          name: "Extended Open Status",
          description: `Shipment has been in "Open" status for ${daysSinceInit} days`,
          severity: "medium",
          icon: faExclamationCircle,
          impact:
            "Potential financial implications and delayed revenue recognition",
          details:
            "Typical time to close status is 10-12 days for similar shipments.",
        });
        recommendations.push({
          id: "verify_payment",
          name: "Verify Payment Status",
          description: "Verify payment status and follow up if necessary",
          icon: faBalanceScale,
          action: "Verify payment",
        });
        score += 20;
      }
    }

    // 4. Entry point performance
    const entryPoint = shipment.header.pedimento.pedimento_entry_point;
    const entryPointPerformance = getEntryPointPerformance(entryPoint);
    if (entryPointPerformance.status === "congested") {
      factors.push({
        id: "congested_entry_point",
        name: "Congested Entry Point",
        description: `Entry point ${entryPoint} is currently experiencing high congestion`,
        severity: "medium",
        icon: faExclamationCircle,
        impact: "Potential crossing delays of 2-3 days",
        details: `Recent volume increase of ${entryPointPerformance.volumeChange}% at this entry point.`,
      });
      recommendations.push({
        id: "monitor_entry_point",
        name: "Monitor Entry Point Status",
        description: "Set up daily monitoring of entry point congestion status",
        icon: faChartLine,
        action: "Set up monitoring",
      });
      score += 15;
    }

    // 5. Check for carrier reliability
    const mexicanCarrier =
      shipment.header.detail?.traffic_data?.mexican_carrier;
    const carrierReliability = getCarrierReliability(mexicanCarrier);
    if (carrierReliability < 85) {
      factors.push({
        id: "carrier_reliability",
        name: "Carrier Reliability Concerns",
        description: `Carrier "${mexicanCarrier}" has below-average reliability score (${carrierReliability}%)`,
        severity: "medium",
        icon: faExclamationCircle,
        impact: "Increased risk of transit delays",
        details: "Based on historical performance over the last 90 days.",
      });
      recommendations.push({
        id: "carrier_communication",
        name: "Enhance Carrier Communication",
        description: "Establish more frequent check-ins with carrier",
        icon: faHandshake,
        action: "Schedule check-ins",
      });
      score += 15;
    }

    // Generate risk score and level
    if (factors.length === 0) {
      factors.push({
        id: "no_risks",
        name: "No Significant Risks Detected",
        description:
          "This shipment is proceeding as expected with no significant risk factors.",
        severity: "none",
        icon: faCheckCircle,
        impact: "None",
        details: "All processes are within normal parameters.",
      });

      recommendations.push({
        id: "standard_monitoring",
        name: "Standard Monitoring",
        description: "Continue standard monitoring procedures",
        icon: faClipboardCheck,
        action: "No action needed",
      });
    }

    // Get historical performance
    const historical = {
      entryPoint: {
        name: entryPoint,
        avgProcessingTime: 280, // hours
        congestionLevel:
          entryPointPerformance.status === "congested" ? "High" : "Normal",
        volumeTrend:
          entryPointPerformance.volumeChange > 0 ? "Increasing" : "Stable",
        recentDelays: entryPointPerformance.status === "congested",
      },
      carrier: {
        name: mexicanCarrier,
        reliabilityScore: carrierReliability,
        onTimeDelivery: carrierReliability - 5,
        documentationAccuracy: carrierReliability + 3,
      },
      customer: {
        name: shipment.header.customer_name,
        avgProcessingTime: 260, // hours
        completionRate: 94, // percentage
        shipmentVolume: "Medium",
      },
      operationType: {
        type: getOperationTypeText(shipment.header.operation_type),
        avgProcessingTime: 270, // hours
        complexityLevel: "Medium",
        typicalRiskFactors: ["Documentation", "Customs Delays"],
      },
    };

    return {
      score: Math.min(100, score),
      factors,
      recommendations,
      historical,
    };
  };

  // Helper function to get entry point performance
  const getEntryPointPerformance = (entryPoint) => {
    // This would be replaced with an actual API call or data lookup
    const mockData = {
      160: { status: "normal", volumeChange: 0 },
      240: { status: "congested", volumeChange: 25 },
      430: { status: "normal", volumeChange: -5 },
      510: { status: "normal", volumeChange: 3 },
      520: { status: "congested", volumeChange: 18 },
      521: { status: "normal", volumeChange: 2 },
      640: { status: "normal", volumeChange: -2 },
    };

    return mockData[entryPoint] || { status: "normal", volumeChange: 0 };
  };

  // Helper function to get carrier reliability score
  const getCarrierReliability = (carrier) => {
    // This would be replaced with an actual API call or data lookup
    if (!carrier) return 90;

    // Just for demo - in real implementation, would come from historical data
    const firstChar = carrier.charAt(0).toLowerCase();
    if (firstChar < "h") return 78;
    if (firstChar < "p") return 88;
    return 92;
  };

  // Helper function to get operation type text
  const getOperationTypeText = (operationType) => {
    if (operationType === "1") return "Import";
    if (operationType === "2") return "Export";
    return operationType;
  };

  // Render loading state while analyzing
  if (isAnalyzing) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <FontAwesomeIcon
          icon={faSpinner}
          className="text-blue-500 text-4xl animate-spin mb-4"
        />
        <p className="text-gray-600">Analyzing shipment risks...</p>
      </div>
    );
  }

  // Determine risk level from score
  const getRiskLevel = (score) => {
    if (score < 20) return { label: "Low", color: "green" };
    if (score < 50) return { label: "Medium", color: "yellow" };
    if (score < 75) return { label: "High", color: "orange" };
    return { label: "Critical", color: "red" };
  };

  const riskLevel = getRiskLevel(riskScore);

  return (
    <div className="space-y-4">
      {/* Risk Score Overview */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-sm font-medium text-gray-900 flex items-center">
            <FontAwesomeIcon
              icon={faShieldAlt}
              className="mr-2 text-blue-500"
            />
            Risk Assessment
          </h3>
          <button
            onClick={analyzeShipmentRisk}
            className="text-xs text-blue-600 hover:text-blue-800 flex items-center"
          >
            <FontAwesomeIcon icon={faHistory} className="mr-1" />
            Refresh Analysis
          </button>
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          {/* Risk Score */}
          <div className="flex-1 bg-gray-50 rounded-lg p-4 text-center">
            <div className="text-sm text-gray-500 mb-1">Overall Risk Score</div>
            <div className="flex items-center justify-center">
              <div
                className={`w-20 h-20 rounded-full flex items-center justify-center border-4 ${
                  riskLevel.color === "green"
                    ? "border-green-500 bg-green-100"
                    : riskLevel.color === "yellow"
                    ? "border-yellow-500 bg-yellow-100"
                    : riskLevel.color === "orange"
                    ? "border-orange-500 bg-orange-100"
                    : "border-red-500 bg-red-100"
                }`}
              >
                <span className="text-2xl font-bold">{riskScore}</span>
              </div>
            </div>
            <div
              className={`mt-2 font-medium ${
                riskLevel.color === "green"
                  ? "text-green-700"
                  : riskLevel.color === "yellow"
                  ? "text-yellow-700"
                  : riskLevel.color === "orange"
                  ? "text-orange-700"
                  : "text-red-700"
              }`}
            >
              {riskLevel.label} Risk
            </div>
          </div>

          {/* Key Risk Factors Summary */}
          <div className="flex-1 bg-gray-50 rounded-lg p-4">
            <div className="text-sm text-gray-500 mb-2">Key Risk Factors</div>
            <div className="space-y-2">
              {riskFactors.slice(0, 3).map((factor) => (
                <div
                  key={factor.id}
                  className="flex items-center text-sm cursor-pointer hover:bg-gray-100 p-1 rounded"
                  onClick={() => setSelectedFactor(factor)}
                >
                  <FontAwesomeIcon
                    icon={factor.icon}
                    className={`mr-2 ${
                      factor.severity === "high"
                        ? "text-red-500"
                        : factor.severity === "medium"
                        ? "text-orange-500"
                        : factor.severity === "low"
                        ? "text-yellow-500"
                        : "text-green-500"
                    }`}
                  />
                  <span className="truncate">{factor.name}</span>
                </div>
              ))}
              {riskFactors.length > 3 && (
                <div className="text-xs text-blue-600 text-right mt-1">
                  +{riskFactors.length - 3} more factors
                </div>
              )}
            </div>
          </div>

          {/* Top Recommendations */}
          <div className="flex-1 bg-gray-50 rounded-lg p-4">
            <div className="text-sm text-gray-500 mb-2">
              Recommended Actions
            </div>
            <div className="space-y-2">
              {recommendations.slice(0, 3).map((rec) => (
                <div key={rec.id} className="flex items-center text-sm">
                  <FontAwesomeIcon
                    icon={rec.icon}
                    className="mr-2 text-blue-500"
                  />
                  <span className="truncate">{rec.name}</span>
                </div>
              ))}
              {recommendations.length > 3 && (
                <div className="text-xs text-blue-600 text-right mt-1">
                  +{recommendations.length - 3} more recommendations
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Selected Risk Factor Details */}
      {selectedFactor && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-md font-medium text-gray-900 flex items-center">
              <FontAwesomeIcon
                icon={selectedFactor.icon}
                className={`mr-2 ${
                  selectedFactor.severity === "high"
                    ? "text-red-500"
                    : selectedFactor.severity === "medium"
                    ? "text-orange-500"
                    : selectedFactor.severity === "low"
                    ? "text-yellow-500"
                    : "text-green-500"
                }`}
              />
              {selectedFactor.name}
            </h3>
            <button
              onClick={() => setSelectedFactor(null)}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              {selectedFactor.description}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-gray-500">Impact:</span>
                <p className="font-medium">{selectedFactor.impact}</p>
              </div>
              <div>
                <span className="text-gray-500">Severity:</span>
                <p
                  className={`font-medium ${
                    selectedFactor.severity === "high"
                      ? "text-red-600"
                      : selectedFactor.severity === "medium"
                      ? "text-orange-600"
                      : selectedFactor.severity === "low"
                      ? "text-yellow-600"
                      : "text-green-600"
                  }`}
                >
                  {selectedFactor.severity.charAt(0).toUpperCase() +
                    selectedFactor.severity.slice(1)}
                </p>
              </div>
            </div>

            <div className="bg-gray-50 p-3 rounded-md">
              <span className="text-xs text-gray-500 block mb-1">
                Additional Details:
              </span>
              <p className="text-sm">{selectedFactor.details}</p>
            </div>

            <div className="pt-2 border-t border-gray-100">
              <div className="text-sm font-medium text-gray-700 mb-2">
                Recommended Actions:
              </div>
              <div className="space-y-2">
                {recommendations
                  .filter(
                    (rec) =>
                      // Simple matching logic - in production would be more sophisticated
                      rec.id.includes(selectedFactor.id.split("_")[0]) ||
                      rec.id.includes(selectedFactor.id.split("_")[1])
                  )
                  .map((rec) => (
                    <div
                      key={rec.id}
                      className="flex items-start text-sm bg-blue-50 p-2 rounded"
                    >
                      <FontAwesomeIcon
                        icon={rec.icon}
                        className="mr-2 text-blue-500 mt-0.5"
                      />
                      <div>
                        <div className="font-medium">{rec.name}</div>
                        <div className="text-xs text-gray-600">
                          {rec.description}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Historical Performance Analysis */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <h3 className="text-sm font-medium text-gray-900 mb-3 border-b pb-2 flex items-center">
          <FontAwesomeIcon icon={faChartLine} className="mr-2 text-blue-500" />
          Historical Performance Analysis
        </h3>

        {/* Entry Point Performance */}
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
            <FontAwesomeIcon
              icon={faMapMarkerAlt}
              className="mr-1 text-gray-500"
            />
            Entry Point Performance
          </h4>
          <div className="bg-gray-50 rounded-md p-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div>
                <span className="text-gray-500 text-xs">Entry Point:</span>
                <p className="font-medium">
                  {historicalPerformance.entryPoint.name}
                </p>
              </div>
              <div>
                <span className="text-gray-500 text-xs">
                  Avg. Processing Time:
                </span>
                <p className="font-medium">
                  {historicalPerformance.entryPoint.avgProcessingTime} hours
                </p>
              </div>
              <div>
                <span className="text-gray-500 text-xs">Congestion Level:</span>
                <p
                  className={`font-medium ${
                    historicalPerformance.entryPoint.congestionLevel === "High"
                      ? "text-orange-600"
                      : "text-green-600"
                  }`}
                >
                  {historicalPerformance.entryPoint.congestionLevel}
                </p>
              </div>
              <div>
                <span className="text-gray-500 text-xs">Volume Trend:</span>
                <p className="font-medium">
                  {historicalPerformance.entryPoint.volumeTrend}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Carrier Performance */}
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
            <FontAwesomeIcon icon={faTruck} className="mr-1 text-gray-500" />
            Carrier Performance
          </h4>
          <div className="bg-gray-50 rounded-md p-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div>
                <span className="text-gray-500 text-xs">Carrier:</span>
                <p className="font-medium">
                  {historicalPerformance.carrier.name || "N/A"}
                </p>
              </div>
              <div>
                <span className="text-gray-500 text-xs">
                  Reliability Score:
                </span>
                <p
                  className={`font-medium ${
                    historicalPerformance.carrier.reliabilityScore >= 90
                      ? "text-green-600"
                      : historicalPerformance.carrier.reliabilityScore >= 80
                      ? "text-yellow-600"
                      : "text-red-600"
                  }`}
                >
                  {historicalPerformance.carrier.reliabilityScore}%
                </p>
              </div>
              <div>
                <span className="text-gray-500 text-xs">On-Time Delivery:</span>
                <p className="font-medium">
                  {historicalPerformance.carrier.onTimeDelivery}%
                </p>
              </div>
              <div>
                <span className="text-gray-500 text-xs">
                  Documentation Accuracy:
                </span>
                <p className="font-medium">
                  {historicalPerformance.carrier.documentationAccuracy}%
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Operation Type Analysis */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
            <FontAwesomeIcon
              icon={faFileInvoiceDollar}
              className="mr-1 text-gray-500"
            />
            Operation Analysis
          </h4>
          <div className="bg-gray-50 rounded-md p-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div>
                <span className="text-gray-500 text-xs">Operation Type:</span>
                <p className="font-medium">
                  {historicalPerformance.operationType.type}
                </p>
              </div>
              <div>
                <span className="text-gray-500 text-xs">
                  Avg. Processing Time:
                </span>
                <p className="font-medium">
                  {historicalPerformance.operationType.avgProcessingTime} hours
                </p>
              </div>
              <div>
                <span className="text-gray-500 text-xs">Complexity Level:</span>
                <p className="font-medium">
                  {historicalPerformance.operationType.complexityLevel}
                </p>
              </div>
              <div>
                <span className="text-gray-500 text-xs">
                  Typical Risk Factors:
                </span>
                <p className="font-medium">
                  {historicalPerformance.operationType.typicalRiskFactors.join(
                    ", "
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Risk Mitigation Suggestions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <h3 className="text-sm font-medium text-gray-900 mb-3 border-b pb-2 flex items-center">
          <FontAwesomeIcon
            icon={faQuestionCircle}
            className="mr-2 text-blue-500"
          />
          Risk Mitigation Suggestions
        </h3>

        <div className="space-y-3">
          {recommendations.map((rec) => (
            <div
              key={rec.id}
              className="flex bg-blue-50 rounded-md p-3 hover:bg-blue-100 transition-colors cursor-pointer"
            >
              <div className="mr-3 text-blue-600">
                <FontAwesomeIcon icon={rec.icon} className="text-lg" />
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-medium text-gray-900">
                  {rec.name}
                </h4>
                <p className="text-xs text-gray-600">{rec.description}</p>
              </div>
              <div>
                <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                  <FontAwesomeIcon icon={faArrowCircleRight} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ShipmentRiskAnalyzer;
